import React from 'react';

import { BRAND_MARKS, isBrandName, type BrandName } from './brandMarks';

/** The pictures the share tiles draw: three kinds of device, the operating systems' marks, then where visitors came
 *  from (each source, and the platforms whose marks read at this size). */
export type ShareGlyphName =
  | 'phone'
  | 'computer'
  | 'tablet'
  | 'device'
  | 'apple'
  | 'android'
  | 'windows'
  | 'system'
  | 'globe'
  | 'bill'
  | 'search'
  | 'cursor'
  | 'social'
  | 'megaphone'
  | 'chat'
  | 'link'
  | 'mail'
  | 'target'
  | 'bookmark'
  | 'question'
  | BrandName;

/** A 24px line or solid glyph, drawn in `paint` (the text colour, or a sweep's `url(#…)`); decorative, the tile names
 *  what it shows. */
export const ShareGlyph = ({
  name,
  paint = 'currentColor',
  size = 22,
}: {
  name: ShareGlyphName;
  paint?: string;
  /** Drawn size in px (the tiles' 22, smaller beside a menu's text). */
  size?: number;
}): React.ReactElement => {
  const line = {
    fill: 'none',
    stroke: paint,
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  let body: React.ReactNode;
  if (isBrandName(name)) {
    // A brand mark fills its 24px box edge to edge; at 80% it reads at the size of the line glyphs beside it.
    const mark = BRAND_MARKS[name];
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <g transform="translate(2.4 2.4) scale(0.8)" fill={paint} fillRule={'evenOdd' in mark ? 'evenodd' : undefined}>
          {mark.paths.map((d) => (
            <path key={d.slice(0, 24)} d={d} />
          ))}
        </g>
      </svg>
    );
  }
  switch (name) {
    case 'phone':
      body = (
        <g {...line}>
          <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
          <path d="M11 18.5h2" />
        </g>
      );
      break;
    case 'computer':
      body = (
        <g {...line}>
          <rect x="3" y="4" width="18" height="12" rx="2" />
          <path d="M9 20h6M12 16v4" />
        </g>
      );
      break;
    case 'tablet':
      body = (
        <g {...line}>
          <rect x="4.5" y="2.5" width="15" height="19" rx="2.5" />
          <path d="M11 18.5h2" />
        </g>
      );
      break;
    case 'device':
      body = (
        <g {...line}>
          <rect x="2.5" y="5" width="13" height="10" rx="1.5" />
          <rect x="16" y="9" width="5.5" height="10" rx="1.5" />
          <path d="M6 19h6M9 15v4" />
        </g>
      );
      break;
    case 'apple':
      body = (
        <path
          fill={paint}
          d="M16.4 12.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.8-.8-3-.8-1.6 0-3 .9-3.8 2.3-1.6 2.8-.4 6.9 1.2 9.2.8 1.1 1.7 2.3 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.8-2.3.9-1.3 1.3-2.6 1.3-2.6s-2.5-1-2.5-3.7zM14.1 5.8c.6-.8 1.1-1.8 1-2.8-.9 0-2 .6-2.7 1.4-.6.7-1.1 1.7-1 2.7 1 .1 2-.5 2.7-1.3z"
        />
      );
      break;
    case 'android':
      body = (
        <>
          <path
            fill={paint}
            fillRule="evenodd"
            d="M3.5 17.5a8.5 8.5 0 0 1 17 0zM9 13.6a1.1 1.1 0 1 0 0-2.2 1.1 1.1 0 0 0 0 2.2zm6 0a1.1 1.1 0 1 0 0-2.2 1.1 1.1 0 0 0 0 2.2z"
          />
          <path {...line} d="M7.2 8.6 5.6 6M16.8 8.6 18.4 6" />
        </>
      );
      break;
    case 'windows':
      body = (
        <path
          fill={paint}
          d="M3 5.6 10.5 4.6v6.9H3zM11.6 4.4 21 3v8.5h-9.4zM3 12.6h7.5v6.9L3 18.4zM11.6 12.6H21V21l-9.4-1.4z"
        />
      );
      break;
    case 'globe':
      body = (
        <g {...line}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
        </g>
      );
      break;
    case 'bill':
      body = (
        <g {...line}>
          <rect x="2.5" y="6" width="19" height="12" rx="2" />
          <circle cx="12" cy="12" r="2.6" />
          <path d="M6 9.5v.01M18 14.5v.01" strokeWidth={2.4} />
        </g>
      );
      break;
    case 'search':
      body = (
        <g {...line}>
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="M15.5 15.5 20.5 20.5" />
        </g>
      );
      break;
    case 'cursor':
      body = (
        <g {...line}>
          <path d="M5.5 4 18 10.2l-5.4 1.9-1.9 5.4z" />
          <path d="M13 13l5.5 5.5" />
        </g>
      );
      break;
    case 'social':
      body = (
        <g {...line}>
          <circle cx="6" cy="12" r="2.6" />
          <circle cx="18" cy="6" r="2.6" />
          <circle cx="18" cy="18" r="2.6" />
          <path d="M8.3 10.8l7.4-3.6M8.3 13.2l7.4 3.6" />
        </g>
      );
      break;
    case 'megaphone':
      body = (
        <g {...line}>
          <path d="M4 10v4a1 1 0 0 0 1 1h2l6 4V5L7 9H5a1 1 0 0 0-1 1z" />
          <path d="M16.5 9a4 4 0 0 1 0 6M7 15l1.2 4.5" />
        </g>
      );
      break;
    case 'chat':
      body = (
        <g {...line}>
          <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H10l-4.5 4v-4h-1A.5.5 0 0 1 4 15.5z" />
          <path d="M8.5 10h.01M12 10h.01M15.5 10h.01" strokeWidth={2.4} />
        </g>
      );
      break;
    case 'link':
      body = (
        <g {...line}>
          <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1.2 1.2" />
          <path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1.2-1.2" />
        </g>
      );
      break;
    case 'mail':
      body = (
        <g {...line}>
          <rect x="3" y="5.5" width="18" height="13" rx="2" />
          <path d="M3.5 7l8.5 6 8.5-6" />
        </g>
      );
      break;
    case 'target':
      body = (
        <g {...line}>
          <circle cx="12" cy="12" r="8.5" />
          <circle cx="12" cy="12" r="4.5" />
          <path d="M12 12h.01" strokeWidth={2.6} />
        </g>
      );
      break;
    case 'bookmark':
      body = (
        <g {...line}>
          <path d="M7 3.5h10a1 1 0 0 1 1 1v16l-6-4-6 4v-16a1 1 0 0 1 1-1z" />
        </g>
      );
      break;
    case 'question':
      body = (
        <g {...line}>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.6 9.3a2.5 2.5 0 0 1 4.8 1c0 1.7-2.4 2.2-2.4 3.7" />
          <path d="M12 17h.01" strokeWidth={2.4} />
        </g>
      );
      break;
    case 'system':
      body = (
        <g {...line}>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 12h.01M12 12h.01M16 12h.01" strokeWidth={2.4} />
        </g>
      );
      break;
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {body}
    </svg>
  );
};

export default ShareGlyph;
